import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';

const COLORS = ['#66BB6A', '#FFA726', '#EF5350'];

// Helper para extrair código (suporta objetos com .codigo ou strings)
const getCodigo = (item) => (item && (item.codigo ?? item));

export default function EntregadoresChart() {
  const [groupedData, setGroupedData] = useState([]);
  const [allEntregadores, setAllEntregadores] = useState([]);
  const [bases, setBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [visibleDialog, setVisibleDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogCodigos, setDialogCodigos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://selenium.lc-transportes.com/token/c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: null,
          senha: null,
          token: localStorage.getItem('accessToken'),
        }),
      });
      if (!response.ok) throw new Error(`Erro na requisição: ${response.status}`);

      const result = await response.json();

      // Agrupar por base e coletar códigos por status e todos pacotes
      const basesMap = {};
      const basesSet = new Set();
      result.forEach((item) => {
        const base = item.base || 'Desconhecida';
        basesSet.add(base);
        if (!basesMap[base])
          basesMap[base] = {
            codigosAll: [],
            totalPrazo: 0,
            codigosPrazo: [],
            totalVencendo: 0,
            codigosVencendo: [],
            totalAtrasado: 0,
            codigosAtrasado: [],
          };
        // Todos pacotes
        const pacotesCod = item.pacotes?.map(getCodigo) || [];
        basesMap[base].codigosAll.push(...pacotesCod);
        // No Prazo
        basesMap[base].totalPrazo += item.prazo?.length || 0;
        basesMap[base].codigosPrazo.push(...(item.prazo?.map(getCodigo) || []));
        // Vencendo
        basesMap[base].totalVencendo += item.vencendo?.length || 0;
        basesMap[base].codigosVencendo.push(...(item.vencendo?.map(getCodigo) || []));
        // Atrasado
        basesMap[base].totalAtrasado += item.atrasado?.length || 0;
        basesMap[base].codigosAtrasado.push(...(item.atrasado?.map(getCodigo) || []));
      });

      // Monta groupedData, incluindo dados para tabela e para gráfico de pizza
      const grouped = Object.entries(basesMap).map(([base, counts]) => {
        const {
          codigosAll,
          totalPrazo,
          codigosPrazo,
          totalVencendo,
          codigosVencendo,
          totalAtrasado,
          codigosAtrasado,
        } = counts;
        const totalStatus = totalPrazo + totalVencendo + totalAtrasado;

        // Tabela
        const table = [
          { status: 'Pacotes', count: codigosAll.length, codigos: codigosAll },
          { status: 'No Prazo', count: totalPrazo, codigos: codigosPrazo },
          { status: 'Vencendo', count: totalVencendo, codigos: codigosVencendo },
          { status: 'Atrasado', count: totalAtrasado, codigos: codigosAtrasado },
        ];

        // Dados para gráfico de pizza (excluindo “Pacotes”)
        const pieData = table
          .filter((row) => row.status !== 'Pacotes')
          .map((row) => ({
            name: row.status,
            value: row.count,
            percent: totalStatus > 0 ? ((row.count / totalStatus) * 100).toFixed(1) : '0.0',
          }));

        return { base, table, pieData };
      });

      // Lista de entregadores para a seção “Todos os Entregadores”
      const entregadoresList = result.map((item) => ({
        base: item.base,
        entregador: item.entregador,
        pacotes: item.pacotes?.length || 0,
        prazo: item.prazo?.length || 0,
        vencendo: item.vencendo?.length || 0,
        atrasado: item.atrasado?.length || 0,
        codigos: item.pacotes?.map(getCodigo) || [],
      }));

      const basesArray = Array.from(basesSet);
      setBases(basesArray);
      // Define primeira base selecionada como padrão, se existir
      setSelectedBase(basesArray[0] || null);
      setGroupedData(grouped);
      setAllEntregadores(entregadoresList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (title, codigos) => {
    setDialogTitle(title);
    setDialogCodigos(codigos);
    setVisibleDialog(true);
  };

  // Filtra groupedData pela base selecionada
  const groupedFiltered = selectedBase
    ? groupedData.filter((g) => g.base === selectedBase)
    : [];

  // Filtra lista de entregadores pela base selecionada
  const filteredEntregadores = selectedBase
    ? allEntregadores.filter((e) => e.base === selectedBase)
    : allEntregadores;

  return (
    <div className="relative flex flex-col w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0 p-4">
      {/* Cabeçalho e botão de atualizar */}
      <div className="rounded-t bg-white mb-4 px-6 py-4 shadow-md">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <h6 className="text-blueGray-700 text-xl uppercase font-bold">
            Contagem
          </h6>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
          
             
              <Dropdown
                value={selectedBase}
                options={bases}
                onChange={(e) => setSelectedBase(e.value)}
                placeholder="Todas as bases"
                showClear
                className="w-64 shadow"
              />
            

          </div>
        </div>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>


      {/* Dropdown para escolher a base */}


      {loading && <p>Carregando dados...</p>}

      {!loading && !error && groupedFiltered.length === 0 && (
        <p className="text-center">Sem dados para exibir.</p>
      )}
<Button
        label="Atualizar Dados"
        icon="pi pi-refresh"
        className="bg-emerald-500 text-white px-4 py-2 rounded-md shadow hover:bg-green-600 font-bold"
        onClick={fetchData}
      />
      {!loading &&
        !error &&
        groupedFiltered.map(({ base, table, pieData }) => (
          <div key={base} className="bg-white p-6 rounded-lg shadow mb-8">
           
            {/* Layout flex: tabela à esquerda, gráfico de pizza à direita */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* DataTable */}
              <div className="flex-1">
                <DataTable
                  value={table}
                  responsiveLayout="scroll"
                  style={{ width: '100%' }}
                >
                  <Column field="status" header="Status / Pacotes" />
                  <Column
                    header="Quantidade"
                    body={(rowData) => (
                      <span
                        style={{ cursor: 'pointer', color: '#007ad9' }}
                        onClick={() =>
                          openDialog(`Base ${base} - ${rowData.status}`, rowData.codigos)
                        }
                      >
                        {rowData.count}
                      </span>
                    )}
                  />
                  <Column
                    header="Percentual"
                    body={(rowData) => {
                      if (rowData.status === 'Pacotes') return '-';
                      const entry = pieData.find((p) => p.name === rowData.status);
                      return entry ? `${entry.percent}%` : '0%';
                    }}
                  />
                </DataTable>
              </div>

              {/* Gráfico de pizza */}
              <div className="flex-1" style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      label={(entry) => `${entry.percent}%`}
                      innerRadius={50}
                      outerRadius={80}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend verticalAlign="bottom" height={30} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ))}

      {/* Seção "Todos os Entregadores" */}
      {!loading && !error && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-2xl font-bold uppercase mb-4">Todos os Entregadores</h4>

          <DataTable
            value={filteredEntregadores}
            responsiveLayout="scroll"
            style={{ width: '100%' }}
          >
            <Column field="base" header="Base" />
            <Column field="entregador" header="Entregador" />
            <Column
              field="pacotes"
              header="Pacotes"
              body={(row) => (
                <span
                  style={{ cursor: 'pointer', color: '#007ad9' }}
                  onClick={() => openDialog(`${row.entregador} - Pacotes`, row.codigos)}
                >
                  {row.pacotes}
                </span>
              )}
            />
            <Column
              field="prazo"
              header="No Prazo"
              body={(row) => (
                <span
                  style={{ cursor: 'pointer', color: '#007ad9' }}
                  onClick={() => openDialog(`${row.entregador} - No Prazo`, row.codigos)}
                >
                  {row.prazo}
                </span>
              )}
            />
            <Column
              field="vencendo"
              header="Vencendo"
              body={(row) => (
                <span
                  style={{ cursor: 'pointer', color: '#007ad9' }}
                  onClick={() => openDialog(`${row.entregador} - Vencendo`, row.codigos)}
                >
                  {row.vencendo}
                </span>
              )}
            />
            <Column
              field="atrasado"
              header="Atrasado"
              body={(row) => (
                <span
                  style={{ cursor: 'pointer', color: '#007ad9' }}
                  onClick={() => openDialog(`${row.entregador} - Atrasado`, row.codigos)}
                >
                  {row.atrasado}
                </span>
              )}
            />
          </DataTable>
        </div>
      )}

      {/* Dialog para exibir códigos */}
      <Dialog
        header={dialogTitle}
        visible={visibleDialog}
        style={{ width: '30vw' }}
        onHide={() => setVisibleDialog(false)}
      >
        <ul>
          {dialogCodigos.map((codigo, idx) => (
            <li key={idx}>{codigo}</li>
          ))}
        </ul>
      </Dialog>
      
    </div>
  );
}
